import { useSelector } from 'store';
import useAuth from './useAuth';
import useErrorSnackbar from './useErrorSnackbar';
import useSendOnboarding from './useSendOnboarding';
import { Contact } from 'types/contact';
import useSuccessSnackbar from './useSuccessSnackbar';
import notifyCaptureSuppressed from 'utils/notifyCaptureSuppressed';

export interface FirebaseEmailTargetId {
  targetId: string; // the contact to find
  message: {
    subject: string;
    html: string;
  };
}

export default function useSendEmailById() {
  const { db, user } = useAuth();
  const mailRef = db.collection('mail');
  const { emailActive } = useSelector((state) => state.config?.mainConfig?.app || {});
  const captureNotifications = useSelector(
    (state) => (state.config?.mainConfig as any)?.notificationSettings?.captureNotifications === true
  );
  const errorSnackbar = useErrorSnackbar();
  const successSnackbar = useSuccessSnackbar();
  const sendOnboarding = useSendOnboarding();

  const sendEmailById = async (emailObject: FirebaseEmailTargetId, checkOnboarding?: boolean) => {
    if (!emailObject.targetId) {
      return Promise.resolve(); // Return resolved promise if no targetId
    }

    // --- CAPTURE ---
    // config/notificationSettings.captureNotifications on → log, don't deliver.
    if (captureNotifications) {
      console.log('[CAPTURE] email suppressed — not delivered', {
        targetId: emailObject.targetId,
        subject: emailObject.message?.subject
      });
      notifyCaptureSuppressed('Email');
      return Promise.resolve();
    }

    if (!emailActive && user?.isStaff) {
      errorSnackbar({ message: 'Email is not active' } as Error);
      return Promise.reject(new Error('Email is not active'));
    }

    const contact = await db
      .collection('contacts')
      .doc(emailObject.targetId)
      .get()
      .then((doc) => {
        return { ...doc.data(), id: doc.id } as Contact;
      })
      .catch((error) => {
        console.error('Error getting email address: ', error);
        return null;
      });

    if (contact && checkOnboarding && !contact?.onboardingSent) {
      console.log('Sending onboarding email');
      sendOnboarding(contact);
    }

    if (contact && contact.email1) {
      return mailRef
        .add({ ...emailObject, to: contact.email1 })
        .then((docRef) => {
          console.log('Email sent with ID: ', docRef.id);
          if (user?.isStaff) {
            successSnackbar('Email(s) sent');
          }
          return docRef;
        })
        .catch((error) => {
          errorSnackbar(error);
          return Promise.reject(error);
        });
    } else {
      return Promise.resolve(); // Resolve if no email to send
    }
  };

  return sendEmailById;
}
